import { computed, inject, nextTick, provide, ref, watch } from 'vue';
import type {
	CollapseContext,
	CollapseItemInstance,
	CollapseKey,
	CollapseValue,
} from './Collapse.model';
import { collapseContextKey } from './Collapse.model';

type CollapseRootProps = {
	modelValue?: CollapseValue;
	active?: CollapseValue;
	accordion: boolean;
	multipleActive: boolean;
	activeIndex: number;
	animationDuration: number;
};

type CollapseRootEmit = (
	event: 'update:modelValue' | 'change',
	value: CollapseValue
) => void;

const toKeys = (value: CollapseValue): CollapseKey[] => {
	if (value === null || value === undefined) {
		return [];
	}
	return Array.isArray(value) ? value : [value];
};

const isUncontrolled = (modelValue: CollapseValue, active: CollapseValue) =>
	modelValue === null && active === null;

export const useCollapseRoot = (
	props: CollapseRootProps,
	emit: CollapseRootEmit
) => {
	const items = ref<CollapseItemInstance[]>([]);
	const internalValue = ref<CollapseKey[]>([]);

	const isMultiple = computed(() => !props.accordion && props.multipleActive);

	const currentValue = computed<CollapseValue>(() => {
		if (props.modelValue !== null) {
			return props.modelValue;
		}
		if (props.active !== null) {
			return props.active;
		}
		return internalValue.value;
	});

	const deactivateAll = () => {
		items.value.forEach((item) => {
			item.setActive(false);
		});
	};

	const activateItemByIndex = () => {
		deactivateAll();
		if (props.activeIndex !== -1 && items.value[props.activeIndex]) {
			items.value[props.activeIndex].setActive(true);
		}
	};

	const syncActiveFromValue = (value: CollapseValue) => {
		const keys = toKeys(value);
		if (keys.length === 0) {
			activateItemByIndex();
			return;
		}

		items.value.forEach((item) => {
			item.setActive(keys.includes(item.itemKey));
		});
	};

	const updateValue = (next: CollapseValue) => {
		if (isUncontrolled(props.modelValue, props.active)) {
			internalValue.value = toKeys(next);
		}
		emit('update:modelValue', next);
		emit('change', next);
		syncActiveFromValue(next);
	};

	const addItem = (item: CollapseItemInstance) => {
		items.value.push(item);
		syncActiveFromValue(currentValue.value);
	};

	const removeItem = (item: CollapseItemInstance) => {
		const index = items.value.indexOf(item);
		if (index > -1) {
			items.value.splice(index, 1);
		}
	};

	const toggleItem = (item: CollapseItemInstance) => {
		const key = item.itemKey;

		if (isMultiple.value) {
			const nextKeys = toKeys(currentValue.value);
			const index = nextKeys.indexOf(key);
			if (index === -1) {
				nextKeys.push(key);
			} else {
				nextKeys.splice(index, 1);
			}
			updateValue(nextKeys);
			return;
		}

		const activeKey = Array.isArray(currentValue.value)
			? (currentValue.value[0] ?? null)
			: currentValue.value;
		updateValue(activeKey === key ? null : key);
	};

	const collapseContext: CollapseContext = {
		isCollapseParent: true,
		get animationDuration() {
			return props.animationDuration;
		},
		get isMultiple() {
			return isMultiple.value;
		},
		addItem,
		removeItem,
		deactivateAll,
		toggleItem,
	};

	provide(collapseContextKey, collapseContext);

	nextTick(() => {
		if (props.modelValue !== null || props.active !== null) {
			syncActiveFromValue(currentValue.value);
			return;
		}
		activateItemByIndex();
	});

	watch(
		() => props.modelValue,
		(newValue) => {
			syncActiveFromValue(newValue);
		}
	);

	watch(
		() => props.active,
		(newValue) => {
			syncActiveFromValue(newValue);
		}
	);

	watch(
		() => props.activeIndex,
		() => {
			if (isUncontrolled(props.modelValue, props.active)) {
				activateItemByIndex();
			}
		}
	);

	return {
		isMultiple,
	};
};

export const useCollapseContext = () => {
	return inject(collapseContextKey, null);
};
